import { useCallback, useEffect, useState } from "react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { OperatingAsBanner } from "@/components/common/OperatingAsBanner";
import { requireSupabase, supabase } from "@/lib/supabase";
import { LocationToolsContent } from "./LocationToolsContent";
import { GoogleCalendarConnectSection } from "./GoogleCalendarConnectSection";
import { SalesChecklistOrgEditor } from "./SalesChecklistOrgEditor";
import { useOrganizationName } from "@/hooks/useOrganizationName";

type LocationToolsSection = "links" | "checklist";

/**
 * Workspace mode: location-scoped utilities (quick links, future lot tools).
 * Embedded in the lead workspace shell: no full-viewport min-height; settings gear stays in the shared header.
 */
export function LocationToolsPanel() {
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const effectiveOrgId = scopedOrgId;
  const resolvedOrgName = useOrganizationName(effectiveOrgId);

  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [activeSection, setActiveSection] = useState<LocationToolsSection | null>(null);

  const loadOrgAndLocations = useCallback(async () => {
    if (!effectiveOrgId || !supabase) {
      setLocations([]);
      return;
    }
    const sb = requireSupabase();
    let q = await sb.from("locations").select("id, name").eq("organization_id", effectiveOrgId).order("name");
    if (q.error) {
      q = await sb.from("locations").select("id, name").eq("org_id", effectiveOrgId).order("name");
    }
    if (!q.error && q.data) {
      setLocations(q.data as { id: string; name: string }[]);
    } else {
      setLocations([]);
    }
  }, [effectiveOrgId]);

  useEffect(() => {
    void loadOrgAndLocations();
  }, [loadOrgAndLocations]);

  const displayOrgName = resolvedOrgName ?? "Selected organization";
  const roleLabel =
    auth.role === "dev"
      ? "Platform Dev"
      : auth.role === "admin" || auth.role === "owner"
        ? "Org Admin"
        : auth.role === "trainer"
          ? "Trainer"
          : auth.role === "marketing"
            ? "Marketing"
            : "User";

  // Container/padding doctrine \u00a7K1+K6: dropped the inner `max-w-6xl mx-auto`
  // ring. Shared <OperatingAsBanner> replaces the inlined amber banner.
  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "lt-surface" }} className="w-full text-left space-y-4">
      <OperatingAsBanner roleLabel={roleLabel} orgName={displayOrgName} />

      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Location Tools</h1>
        <div className="text-xs text-gray-400 dark:text-gray-500 font-mono mt-0.5">
          Scoped org: {scopedOrgId ?? "\u2014"}
        </div>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 max-w-3xl">
        Configure per-org defaults and per-location shortcuts used from the Leads workspace. Settings (theme, workspace)
        stay in the header gear.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          type="button"
          {...{ [FIREFLY_TARGET_ATTR]: "lt-links-tile" }}
          onClick={() => setActiveSection((prev) => (prev === "links" ? null : "links"))}
          className={
            "rounded-xl border bg-white dark:bg-gray-900/50 p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60 " +
            (activeSection === "links"
              ? "border-blue-300 shadow-[0_0_0_1px_rgba(59,130,246,0.22)] dark:border-blue-700/80"
              : "border-gray-200 dark:border-gray-700")
          }
        >
          <div className="font-semibold text-gray-900 dark:text-gray-100">Location links</div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Defaults, custom types, and per-lot URLs
          </div>
        </button>
        <button
          type="button"
          onClick={() => setActiveSection((prev) => (prev === "checklist" ? null : "checklist"))}
          className={
            "rounded-xl border bg-white dark:bg-gray-900/50 p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60 " +
            (activeSection === "checklist"
              ? "border-blue-300 shadow-[0_0_0_1px_rgba(59,130,246,0.22)] dark:border-blue-700/80"
              : "border-gray-200 dark:border-gray-700")
          }
        >
          <div className="font-semibold text-gray-900 dark:text-gray-100">Sales daily checklist</div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Ad slots by platform, keywords, manual tasks &amp; schedules (Leads tab)
          </div>
        </button>
      </div>

      <GoogleCalendarConnectSection organizationId={scopedOrgId} />

      {activeSection ? (
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
          <div className="font-semibold text-gray-900 dark:text-gray-100">
            {activeSection === "links" ? "Location links" : "Sales daily checklist"}
          </div>
          {activeSection === "links" ? (
            <LocationToolsContent
              orgId={scopedOrgId}
              locations={locations}
              onLocationsUpdated={() => void loadOrgAndLocations()}
            />
          ) : scopedOrgId ? (
            <SalesChecklistOrgEditor organizationId={scopedOrgId} />
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select a workspace organization to edit the checklist.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}
